import { useMutation } from '@tanstack/react-query';

import { buildSubmitClaimDTO } from './submitClaimDTO';
import { ClaimContext } from '../../parcel-finder/claims/types';
import useNotifications from '../../../hooks/useNotifications';
import { useSelectedShopId } from '../../../hooks/useSelectedShop';
import { ApiError } from '../../../api/Api';
import container from '../../../container';

// biome-ignore-start lint/style/useNamingConvention: external
const claimTypeToPathSlug = {
  investigation: 'investigation',
  partial_damage: 'partial-damage',
  whole_damage: 'whole-damage',
  missing_item: 'missing-item',
  cash_on_delivery: 'cash-on-delivery',
};
// biome-ignore-end lint/style/useNamingConvention: external

type UseSubmitClaimMutationOptions = {
  claim: ClaimContext;
};

function useSubmitClaimMutation(options: UseSubmitClaimMutationOptions) {
  const { claim } = options;
  const { showNotice, showError } = useNotifications();
  const shopId = useSelectedShopId();

  return useMutation({
    mutationFn: async () => {
      if (!claim.type) {
        throw new Error('Claim type is required to submit a claim');
      }

      const payload = buildSubmitClaimDTO(claim);
      const slug = claimTypeToPathSlug[claim.type];

      return container.touchpointsApi.submitClaim(shopId, slug, payload);
    },
    onSuccess: () => showNotice({ type: 'success', message: 'CLAIMS_SUCCESSFULLY_SUBMITTED_NOTICE' }),
    onError: (error) => showError(getErrorMessage(error)),
  });
}

function getErrorMessage(error: ApiError | unknown): string | undefined {
  if (!(error instanceof ApiError)) {
    return;
  }

  // biome-ignore-start lint/style/useNamingConvention: external
  const errorMessages = {
    claim_exists_for_shipment_id: 'CLAIM_ALREADY_EXISTS_FOR_SHIPMENT',
    claim_exists_for_tracking_code: 'CLAIM_ALREADY_EXISTS_FOR_TRACKING_CODE',
    claim_files_conflict: 'CLAIM_FILES_CONFLICT',
  };
  // biome-ignore-end lint/style/useNamingConvention: external

  // @ts-ignore
  return errorMessages[error?.response?.data?.code];
}

export default useSubmitClaimMutation;
